<template>
  <div
    role="button"
    tabindex="0"
    class="team-item"
    :class="isCurrent ? 'team-item--current' : 'team-item--other'"
    @click="$emit('click')"
    @keydown.enter="$emit('click')"
    @keydown.space.prevent="$emit('click')"
  >
    <DarwinAvatar
      :id="team.id"
      class="team-item__avatar"
      :name="team.name"
      :url="avatarUrl"
    />
    <div class="team-item__team">
      <div class="team-item__team__name">
        {{ team.name }}
      </div>
      <div
        v-if="isCurrent"
        class="team-item__team__members"
      >
        {{ memberCount }} team members
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import DarwinAvatar from '@/uiKit/Avatar/DarwinAvatar.vue'
import type { TeamPayload } from '@/store/types'
import { computed } from 'vue'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useMembershipStore } from '@/modules/Teams/useMembershipStore'

const props = defineProps<{ team: TeamPayload }>()

defineEmits<{ (e: 'click'): void }>()

const teamStore = useTeamStore()
const membershipStore = useMembershipStore()

const isCurrent = computed(() => props.team.id === teamStore.currentTeam?.id)

const memberCount = computed(() =>
  isCurrent.value ? membershipStore.getMembershipsByTeamId(props.team.id).length : null,
)

const avatarUrl = computed(() => props.team.image?.thumbnail_url || props.team.image?.url || null)
</script>

<style lang="scss" scoped>
.team-item:hover {
  background: var(--color-background-transparent-hovered);
  cursor: pointer;
}

.team-item {
  @include row--center;
  column-gap: 10px;
  align-items: left;
  padding: 6px 10px;
  border-radius: 6px;
  margin-bottom: 4px;

  & &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    outline: 1px solid var(--color-border-subtle);
  }
}

.team-item__team {
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: 1fr;
  width: calc(100% - 54px);
  justify-content: start;
}

.team-item__team__name {
  @include ellipsis(2, md-1);
  @include typographyRegularBody200;
  word-break: break-all;
  color: var(--color-text-default);

  grid-row: 1 / 2;
}

.team-item__team .team-item__team__pill {
  grid-row: 1 / 2;

  align-self: start;
  justify-self: end;
}

.team-item--other .team-item__team__name {
  @include ellipsis(1, md-1);
  @include typographyRegularBody200;
  color: var(--color-text-default);
}

.team-item__team__members {
  @include typography(md-1, headlines);
  @include typographyRegularBody100;
  color: var(--color-text-subtle);
  grid-row: 2 / 3;
}
</style>
